import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles, faClipboardCheck, faChartColumn } from '@fortawesome/free-solid-svg-icons';
import Navigation from './Navigation';
import VideoComparison from './VideoComparison';
import RunScail2 from './RunScail2';
import ProcessingComponent from './ProcessingComponent';
import HeaderSection from "./Header";
import SourceVideo from "./SourceVideo";
import SourceImage from "./SourceImage";
import AnnotationTab from "./AnnotationTab";
import PrivacyChoice from "./PrivacyChoice";
import AdminResultsTab from "./AdminResultsTab";
import axiosInstance from './utils/axiosinstance';

const LandingPage = () => {
    const [activeTab, setActiveTab] = useState('create');
    const [isReviewAdmin, setIsReviewAdmin] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/whoami/')
            .then((r) => setIsReviewAdmin(!!r.data.is_review_admin))
            .catch(() => setIsReviewAdmin(false));
    }, []);
    const [sourceImage, setSourceImage] = useState(null);
    const [sourceVideo, setSourceVideo] = useState(null);
    const [processedVideo, setProcessedVideo] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [videoDuration, setVideoDuration] = useState(null);

    useEffect(() => {
        if (sourceVideo && sourceImage) {
            const fetchProcessedVideo = async () => {
                try {
                    const response = await axiosInstance.get('/api/fetch_processed_video/', {
                        params: {
                            sourceVideo: sourceVideo.id,
                            sourceImage: sourceImage.id,
                        },
                    });
                    if (response.data) {
                        console.log('Fetched processed video:', response.data.processed_video);
                        setProcessedVideo(response.data.processed_video);
                    }
                } catch (error) {
                    console.error('Error fetching processed video:', error);
                }
            };

            fetchProcessedVideo();
        }
    }, [sourceVideo, sourceImage]);

    const handleSetSourceVideo = (media) => {
        // Store the entire 'media' object in sourceVideo
        setSourceVideo(media);
        // If UploadComponent found a localDuration, set it
        if (media.duration) {
            setVideoDuration(media.duration);
        }
    };

    const handleJobSubmitted = (video) => {
        console.log('Job submitted:', video);
        setProcessedVideo(video);
    };

    const handleProcessingComplete = async (video) => {
        console.log('Processing complete:', video);
        setProcessedVideo(video);
    };

    const handleProcessingError = () => {
        console.log("[LandingPage] Processing error occurred");
        setProcessedVideo(prev => ({
            ...prev,
            status: 'failed',
            s3_key: null
        }));
    };

    const handlePlayPause = () => setPlaying(!playing);

    // change the privacy/consent choice of an already-processed video
    const handleSharedChange = async (shared) => {
        if (!processedVideo?.id) return;
        try {
            const response = await axiosInstance.post('/api/set_processed_shared/', {
                id: processedVideo.id,
                shared: shared,
            });
            setProcessedVideo(response.data.video);
        } catch (error) {
            console.error('Error updating sharing choice:', error);
        }
    };

    const canShowVideoComparison = processedVideo?.exists;
    console.log("[LandingPage] processedVideo?.exists:", canShowVideoComparison);

    const tabClasses = (tab) =>
        `flex items-center gap-3 px-8 py-3 rounded-t-xl text-xl font-bold transition-colors ${
            activeTab === tab
                ? 'bg-white bg-opacity-20 text-white border-b-4 border-sil-yellow-500'
                : 'text-white text-opacity-60 hover:text-opacity-100 hover:bg-white hover:bg-opacity-10'
        }`;

    // Review and Results both get the near-full-screen layout
    const annotate = activeTab === 'annotate'
        || (activeTab === 'results' && isReviewAdmin);

    return (
        <div className={`bg-gradient-to-br from-sil-red-500 to-sil-yellow-500 min-h-screen ${annotate ? 'p-3' : 'p-8'}`}>
            <Navigation />
            {!annotate && <HeaderSection />}

            {/* Review gets nearly the whole screen; Create keeps the centered column */}
            <div className={annotate ? 'w-full px-1' : 'container mx-auto p-4'}>
                {/* icon-first tabs: create (magic wand) / review (clipboard check) */}
                <div className={`flex gap-2 border-b border-white border-opacity-30 ${annotate ? 'mb-3' : 'mb-6'}`}>
                    <button
                        className={tabClasses('create')}
                        onClick={() => setActiveTab('create')}
                        title="Create an anonymized video"
                    >
                        <FontAwesomeIcon icon={faWandMagicSparkles} className="text-2xl" />
                        <span>Create</span>
                    </button>
                    <button
                        className={tabClasses('annotate')}
                        onClick={() => setActiveTab('annotate')}
                        title="Review and annotate videos"
                    >
                        <FontAwesomeIcon icon={faClipboardCheck} className="text-2xl" />
                        <span>Review</span>
                    </button>
                    {isReviewAdmin && (
                        <button
                            className={tabClasses('results')}
                            onClick={() => setActiveTab('results')}
                            title="Annotation results across users (admin)"
                        >
                            <FontAwesomeIcon icon={faChartColumn} className="text-2xl" />
                            <span>Results</span>
                        </button>
                    )}
                </div>

                {activeTab === 'results' && isReviewAdmin ? (
                    <AdminResultsTab />
                ) : activeTab === 'annotate' ? (
                    <AnnotationTab />
                ) : (
                    <>
                        <div
                            className="flex items-center justify-between mb-4 cursor-pointer"
                        >
                            <h2 className="text-lg font-bold text-white">Source Media</h2>
                        </div>
                        <div className="flex flex-wrap lg:flex-nowrap lg:gap-8 transition-all duration-500">
                            <div className="w-full mb-4">
                                <SourceVideo
                                    sourceVideo={sourceVideo}
                                    setSourceVideo={handleSetSourceVideo}
                                    small={processedVideo?.exists}
                                />
                            </div>
                            <div className="w-full mb-4">
                                <SourceImage
                                    sourceImage={sourceImage}
                                    setSourceImage={setSourceImage}
                                    small={processedVideo?.exists}
                                />
                            </div>
                        </div>
                        <div className="mt-8">
                            {processedVideo?.exists ? (
                                <>
                                    <VideoComparison
                                        sourceVideo={sourceVideo}
                                        processedVideo={processedVideo}
                                        playing={playing}
                                        onPlayPause={handlePlayPause}
                                    />
                                    <div className="mt-6">
                                        <PrivacyChoice
                                            shared={!!processedVideo.shared}
                                            onChange={handleSharedChange}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div>
                                    {sourceVideo && sourceImage && !processedVideo && (
                                        <RunScail2
                                            sourceVideo={sourceVideo}
                                            sourceImage={sourceImage}
                                            onSuccess={handleJobSubmitted}
                                        />
                                    )}
                                    {(
                                        processedVideo?.status === 'queued' ||
                                        processedVideo?.status === 'in_progress' ||
                                        processedVideo?.status === 'failed' ||
                                        processedVideo?.status === 'pre-processing'
                                    ) && (
                                            <ProcessingComponent
                                                id={processedVideo.id}
                                                status={processedVideo.status}
                                                setStatus={(status) => setProcessedVideo((prev) => ({ ...prev, status }))}
                                                videoDuration={videoDuration}
                                                onComplete={handleProcessingComplete}
                                                onError={handleProcessingError}
                                            />
                                        )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LandingPage;
